import { useCallback, useSyncExternalStore } from 'react';
import {
  ADVANCED_LOCATION_METRICS,
  DEFAULT_LOCATION_METRICS,
  type MetricsList,
} from '@/features/locations/constants';
import { setStorageItem, STORAGE_KEYS, subscribeToStorageItem } from '@/libs/storage';
import { DEFAULT_RADIUS_METERS } from '../context/ManualPinContext';

function getStoredComputePreferencesJson(): string | null {
  return localStorage.getItem(STORAGE_KEYS.COMPUTE_PREFERENCES);
}

function parseComputePreferencesJson(json: string | null): unknown {
  if (json === null) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Shape persisted under `STORAGE_KEYS.COMPUTE_PREFERENCES`. */
export interface ComputePreferences {
  radiusMeters: number;
  metrics: MetricsList[];
}

function isComputePreferences(value: unknown): value is ComputePreferences {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Partial<ComputePreferences>;
  return (
    typeof candidate.radiusMeters === 'number' &&
    Array.isArray(candidate.metrics) &&
    candidate.metrics.length > 0
  );
}

const DEFAULT_PREFERENCES: ComputePreferences = {
  radiusMeters: DEFAULT_RADIUS_METERS,
  metrics: [...DEFAULT_LOCATION_METRICS, ...ADVANCED_LOCATION_METRICS],
};

/**
 * Reads and writes the map's compute preferences (radius + metrics) applied
 * when a new location is added from search.
 *
 * Subscribes to `localStorage` via `useSyncExternalStore` rather than reading
 * it once into `useState` — other components (e.g. `ComputePreferencesModal`)
 * write this same key, so every caller must re-render when the stored value
 * changes rather than keep a stale snapshot from its own last render.
 */
export function useComputePreferences() {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      subscribeToStorageItem(STORAGE_KEYS.COMPUTE_PREFERENCES, onStoreChange),
    [],
  );
  const storedJson = useSyncExternalStore(subscribe, getStoredComputePreferencesJson);
  const stored = parseComputePreferencesJson(storedJson);
  const preferences = isComputePreferences(stored) ? stored : DEFAULT_PREFERENCES;

  function savePreferences(next: ComputePreferences): void {
    setStorageItem(STORAGE_KEYS.COMPUTE_PREFERENCES, next);
  }

  return { preferences, savePreferences };
}
